import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { gridClasses } from "@mui/x-data-grid-pro-6";
import { GridColDef } from "@mui/x-data-grid-pro-6";
import React, { useMemo } from "react";

import SortedDataGridPro6 from "../../../design/sorted-datagrid-pro-6";
import { JobsMatrixWorker } from "../store/types";
import { JobStatus } from "../store/types";
import ColumnJobCell from "./column-job-cell";
import ColumnJobHeader from "./column-job-header";

interface JobsMatrixTableProps {
    data: JobsMatrixWorker[];
    allJobs: any[];
    jobStatuses: JobStatus[];
    loading: boolean;
    searchText: string;
    workerColumns: GridColDef[];
    handleJobStatusChange: (
        params: any,
        jobId: number,
        statusCode: string,
        setState: React.Dispatch<React.SetStateAction<string | null>>
    ) => void;
    height?: number;
    isFilterOpen?: boolean;
}

const JobsMatrixTable: React.FC<JobsMatrixTableProps> = ({
    data,
    allJobs,
    jobStatuses,
    loading,
    searchText,
    workerColumns,
    handleJobStatusChange,
    height,
    isFilterOpen,
}) => {

    const columns = useMemo(() => {
        // Define sort order for job statuses
        const statusOrder: Record<string, number> = {
            qualified: 0,
            expired: 1,
            not_qualified: 2,
        };

        // Add job columns
        const jobColumns = allJobs.map((job) => ({
            field: `job-${job.id}`,
            headerName: job.name,
            width: 120,
            headerAlign: "center" as const,
            renderHeader: () => <ColumnJobHeader jobName={job.name} />,
            renderCell: (params: any) => (
                <ColumnJobCell
                    params={params}
                    jobId={job.id}
                    handleJobStatusChange={handleJobStatusChange}
                    jobStatuses={jobStatuses}
                />
            ),
            sortComparator: (_v1: any, _v2: any, param1: any, param2: any) => {
                const worker1Jobs = param1.api.getRow(param1.id)?.jobs || [];
                const worker2Jobs = param2.api.getRow(param2.id)?.jobs || [];

                const job1 = worker1Jobs.find((j: any) => j.id === job.id);
                const job2 = worker2Jobs.find((j: any) => j.id === job.id);

                const status1 = job1?.jobStatusCode?.toLowerCase() || "";
                const status2 = job2?.jobStatusCode?.toLowerCase() || "";

                const order1 = statusOrder[status1] ?? 999;
                const order2 = statusOrder[status2] ?? 999;

                return order1 - order2;
            },
        }));

        return [...workerColumns, ...jobColumns];
    }, [allJobs, workerColumns, handleJobStatusChange, jobStatuses]);

    // Get the field names of worker columns for pinning
    const pinnedColumnFields = useMemo(() => {
        return workerColumns.map((col) => col.field);
    }, [workerColumns]);

    const rows = useMemo(() => {
        return data.map((worker) => ({
            id: worker.workerId,
            ...worker,
            workCenterIds: worker.workcenterId ? String(worker.workcenterId) : "",
        }));
    }, [data]);

    return (
        <Box
            sx={{
                width: "100%",
                height: height,
                minWidth: 320,
            }}
        >
            <SortedDataGridPro6
                defaultSortField="name"
                key={`jobs-matrix-datagrid-${rows.length}`}
                pagination
                slots={{
                    loadingOverlay: LinearProgress,
                }}
                loading={loading}
                pageSizeOptions={[25, 50, 100]}
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                getRowHeight={() => 50}
                columnHeaderHeight={60}
                pinnedColumns={{
                    left: pinnedColumnFields,
                }}
                sx={{
                    [`& .${gridClasses.cell}`]: {
                        py: 1,
                    },
                    "& .MuiDataGrid-columnHeader": {
                        padding: 0,
                    },
                    "& .MuiDataGrid-columnHeader:focus": {
                        outline: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "white",
                    },
                    "& .MuiDataGrid-cell:hover": {
                        backgroundColor: "#F8F8F8",
                    },
                    "& .MuiDataGrid-cell": {
                        padding: 0,
                    },
                    "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                    },
                    "& .hideRightSeparator > .MuiDataGrid-columnSeparator": {
                        display: "none",
                    },
                    "& .MuiDataGrid-pinnedColumnHeaders": {
                        height: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-end",
                        boxShadow:
                            "0px 3px 1px -2px rgba(0,0,0,0.0),0px 2px 2px 0px rgba(0,0,0,0.0),0px 1px 5px 0px rgba(0,0,0,0.12)",
                    },
                    backgroundColor: "#fff",
                    border: 0,
                    "& .MuiTablePagination-root": {
                        marginRight: "60px",
                    },
                }}
                disableColumnMenu
            />
        </Box>
    );
};

export default JobsMatrixTable;
